import { TellescopeModelFromName, useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { Enduser } from "@tellescope/types-models";
import { useMemo } from "react";
import { createIdMap, createKeyMap } from "@tellescope/utils";

export interface UseFormDataQueriesArgs {
  formId: string;
  providedFormTitle: string | undefined;
  responseId: string | undefined;
  enduserId: string;
  accessCode: string | undefined;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
}

/**
 * Loads the form, its fields, and any existing response.
 *
 * There are two data sources: an access-code flow fetches everything in one authenticated-by-code
 * request, while the authenticated flow fetches each piece separately. This hook is the only place
 * that distinction is made.
 */
export const useFormDataQueries = ({
  formId,
  providedFormTitle,
  responseId,
  enduserId,
  accessCode,
  enduser,
}: UseFormDataQueriesArgs) => {
  const formQuery = useTellescopeQuery({
    model: "forms",
    method: "getOne",
    request: formId,
    enabled: !providedFormTitle && !!formId && !accessCode,
  });

  const formFieldsQuery = useTellescopeInfiniteQuery({
    model: "form_fields",
    method: "getSome",
    request: {
      filter: {
        formId,
      },
      limit: 500,
    },
    select: (data) => ({
      fieldsArr: data.pages.flat(),
      fieldsMap: data.pages.reduce<Record<string, TellescopeModelFromName<"form_fields">>>((acc, curr) => {
        curr.forEach((field) => {
          acc[field.id] = field;
        });
        return acc;
      }, {}),
    }),
    enabled: !!formId && !accessCode,
  });

  const responseQuery = useTellescopeQuery({
    model: "form_responses",
    method: "getOne",
    request: responseId ?? "",
    enabled: !!responseId && !!formId,
  });

  const enduserResponseInfoQuery = useTellescopeQuery({
    model: "form_responses",
    method: "info_for_access_code",
    request: {
      accessCode: accessCode ?? "",
    },
    enabled: !!accessCode,
    select: (data) => {
      return {
        ...data,
        fields: {
          fieldsArr: data.fields,
          fieldsMap: createIdMap(data.fields),
        },
      };
    },
  });

  const { form, fields, response } = useMemo(() => {
    if (accessCode) return enduserResponseInfoQuery.data ?? {};

    return {
      form: formQuery.data,
      fields: formFieldsQuery.data,
      response: responseQuery.data,
    };
  }, [accessCode, enduserResponseInfoQuery.data, formQuery.data, formFieldsQuery.data, responseQuery.data]);

  const formTitle = providedFormTitle || form?.displayTitle || form?.title;

  const responsesMap = useMemo(() => createKeyMap(response?.responses ?? [], "fieldId"), [response?.responses]);

  const enduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    enabled: !accessCode,
  });

  const enduserForPrepopulation = accessCode ? enduser : enduserQuery.data;

  return {
    form,
    fields,
    response,
    formTitle,
    responsesMap,
    enduserForPrepopulation,
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduserResponseInfoQuery,
    enduserQuery,
  };
};
